/**
 * Sentry Error Tracking Configuration
 * 
 * To enable Sentry:
 * 1. Create a Sentry account at https://sentry.io
 * 2. Create a new React project
 * 3. Add VITE_SENTRY_DSN to your .env.local
 * 
 * Example .env.local:
 * VITE_SENTRY_DSN=https://your-key@sentry.io/your-project-id
 * VITE_SENTRY_ENVIRONMENT=production
 * VITE_SENTRY_TRACES_SAMPLE_RATE=0.1
 */

import * as Sentry from '@sentry/react';
import { ENV } from '@shared/config/env';

const SENTRY_DSN = ENV.SENTRY.DSN;
const SENTRY_ENVIRONMENT = ENV.SENTRY.ENVIRONMENT;
const TRACES_SAMPLE_RATE = ENV.SENTRY.TRACES_SAMPLE_RATE;

/**
 * Initialize Sentry error tracking
 * Only runs in production with a valid DSN
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    if (import.meta.env.PROD) {
      console.warn('[Sentry] No DSN configured. Error tracking disabled.');
    } else {
      console.info('[Sentry] Not configured - add VITE_SENTRY_DSN to enable error tracking');
    }
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      
      // Only track in production (unless explicitly enabled for dev)
      enabled: import.meta.env.PROD || SENTRY_ENVIRONMENT !== 'development',
      
      // Environment tag
      environment: SENTRY_ENVIRONMENT,
      
      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      
      // Performance monitoring sample rate (0 = disabled, 1 = 100%)
      tracesSampleRate: TRACES_SAMPLE_RATE,
      
      // Session replay sample rates
      replaysSessionSampleRate: 0.1, // 10% of normal sessions
      replaysOnErrorSampleRate: 1.0, // 100% of error sessions
      
      // Filter out noise
      ignoreErrors: [
        // Browser extension errors
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        // Network errors that aren't actionable
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        'NetworkError',
        // User cancelled
        'AbortError',
        // Third party script errors
        'Script error.',
        // ResizeObserver (non-critical)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],
      
      // Filter before sending to Sentry
      beforeSend(event, hint) {
        // Don't send in dev mode unless explicitly enabled
        if (import.meta.env.DEV && SENTRY_ENVIRONMENT === 'development') {
          console.warn('[Sentry] Error captured (not sent in dev):', event, hint.originalException);
          return null;
        }
        
        // Remove user email/personal data if present
        if (event.user) {
          delete event.user.email;
          delete event.user.ip_address;
        }
        
        return event;
      },
    });

    console.info('[Sentry] Initialized successfully:', {
      environment: SENTRY_ENVIRONMENT,
      enabled: import.meta.env.PROD || SENTRY_ENVIRONMENT !== 'development',
    });
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Set user context for error tracking
 * Call this after user logs in
 */
export function setSentryUser(user) {
  if (!SENTRY_DSN) return;
  
  Sentry.setUser({
    id: user?.dbId || user?.id,
    username: user?.username,
    role: user?.role,
  });
}

/**
 * Clear user context
 * Call this on logout
 */
export function clearSentryUser() {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
}

/**
 * Manually capture an error with context
 */
export function captureError(error, context = {}) {
  if (!SENTRY_DSN) {
    console.error('[Error]', error, context);
    return;
  }
  
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message, data = {}, category = 'app') {
  if (!SENTRY_DSN) return;
  
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Re-export Sentry's ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary;
